import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const categoryNameMap: Record<string, string> = {
  'skin-care': '肌肤护理',
  'image-enhance': '形象提升',
  'beauty-secrets': '美丽秘籍',
  'mom-stories': '妈妈故事',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.slug === slug);
  const categoryName = categoryNameMap[slug || ''] || slug || '未知分类';
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  const seoTitle = `${categoryName} - 掌中美母 | 妈妈美丽养护`;
  const seoDescription = category
    ? category.description
    : `掌中美母${categoryName}栏目，为妈妈提供专业的美丽养护知识。`;

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        canonicalPath={`/category/${slug}`}
      />

      <section className="py-10 md:py-14 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="mb-8 md:mb-10">
            <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-3">
              {categoryName}
            </h1>
            {category && (
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl">
                {category.description}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 mb-8 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-900'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer"
                >
                  <div className="relative h-44 md:h-48 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4 md:p-5">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-xs text-foreground-400">{article.date}</span>
                      <span className="text-xs text-foreground-400">·</span>
                      <span className="text-xs text-foreground-400">{article.readTime}</span>
                      <span className="text-xs text-foreground-400">·</span>
                      <span className="text-xs text-foreground-400">{article.author}</span>
                    </div>
                    <h2 className="text-sm md:text-base font-heading font-medium text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                      {article.title}
                    </h2>
                    <p className="text-xs text-foreground-500 line-clamp-2">{article.excerpt}</p>
                    <div className="flex items-center gap-1.5 mt-3 flex-wrap">
                      {article.tags.slice(0, 2).map((tag) => (
                        <span
                          key={tag}
                          className="text-xs text-secondary-600 bg-secondary-50 px-2 py-0.5 rounded-full whitespace-nowrap"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-foreground-500 text-sm">该分类下暂无文章</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}